import {
  axiosDeleteRequest,
  axiosGetRequest,
  axiosPatchRequest,
  axiosPostPaginatedRequest,
  axiosPostRequest,
  axiosPutRequest,
  getReferenceRoute,
  getStoriesRoute,
} from '../../api';
import { CreateLikeDto } from '../../common';
import { Post } from '../../community';
import {
  GetMyStoriesDto,
  GetStoriesDto,
  InsertStoryDto,
  ModeratorEditStoryDto,
} from './dtos';
import { FeaturedContentPOSTRequestDTO } from './dtos/FeaturedContentPOSTRequestDTO';
import { FeaturedContentPUTRequestDTO } from './dtos/FeaturedContentPUTRequestDTO';
import { MyStoriesStats, Story, StorySortBy, Tag, Topic } from './model';
import { ShareStoryDto } from './model/ShareStoryDto';
import { UpdateFeaturedStoryDto } from './model/UpdateFeaturedStoryDto';

const PAGE_SIZE = 7;

const getAllStoriesRoute = () => `${getStoriesRoute()}/stories`;
const getStoriesTagsRoute = () => `${getReferenceRoute()}/storytags`;
const getStoriesTopicsRoute = () => `${getReferenceRoute()}/storytopics`;
const getStoriesStatsRoute = () => `${getStoriesRoute()}/stories/stats`;
const getStandardStoryRoute = () => `${getStoriesRoute()}/story`;
const getFeaturedStoryRoute = () => `${getStoriesRoute()}/featuredstory`;
const getFeaturedContentRoute = () => `${getStoriesRoute()}/featuredcontent`;

export const fetchAllStoriesCall = (
  data?: GetStoriesDto,
  xMsContinuationToken?: string,
  numrecs?: number,
  sortBy?: StorySortBy
) => {
  return axiosPostPaginatedRequest<GetStoriesDto, Story[]>(
    `${getAllStoriesRoute()}/${numrecs ?? PAGE_SIZE}?state=published&sortby=${
      sortBy ?? StorySortBy.byDateCreated
    }`,
    data ? data : {},
    xMsContinuationToken
  );
};

export const fetchSuggestedStoriesCall = (
  data?: GetStoriesDto,
  xMsContinuationToken?: string,
  numrecs?: number,
  sortBy?: StorySortBy
) => {
  return axiosPostPaginatedRequest<GetStoriesDto, Story[]>(
    `${getAllStoriesRoute()}/${numrecs ?? PAGE_SIZE}?state=published&sortby=${
      sortBy ?? StorySortBy.byDateCreated
    }&ignoreown=true`,
    data ? data : {},
    xMsContinuationToken
  );
};

export const fetchFollowingStoriesCall = (
  data?: GetStoriesDto,
  xMsContinuationToken?: string,
  numrecs?: number
) => {
  return axiosPostPaginatedRequest<GetStoriesDto, Story[]>(
    `${getAllStoriesRoute()}/${numrecs ?? PAGE_SIZE}?state=published&sortby=${
      StorySortBy.byDateCreated
    }&following=true`,
    data ? data : {},
    xMsContinuationToken
  );
};

export const fetchMyStoriesCall = (
  data?: GetMyStoriesDto,
  xMsContinuationToken?: string,
  numrecs?: number
) => {
  const publishedCondition =
    data?.published !== undefined
      ? data.published === true
        ? `&state=published`
        : `&state=drafted`
      : '';

  return axiosPostPaginatedRequest<GetStoriesDto, Story[]>(
    `${getAllStoriesRoute()}/${numrecs ?? PAGE_SIZE}?memberid=${
      data?.memberid
    }${publishedCondition}&sortby=${StorySortBy.byDateUpdated}`,
    data?.additionalData ?? {},
    xMsContinuationToken
  );
};

export const fetchStoryTagsCall = () => {
  return axiosGetRequest<Tag[]>(getStoriesTagsRoute());
};

export const fetchStoryTopicsCall = () => {
  return axiosGetRequest<Topic[]>(getStoriesTopicsRoute());
};

export const fetchMyStoryStatsCall = () => {
  return axiosGetRequest<MyStoriesStats>(getStoriesStatsRoute());
};

export const fetchFullStoryCall = (storyId: string) => {
  return axiosGetRequest<Story>(`${getStandardStoryRoute()}/${storyId}`);
};

export const insertStoryCall = (data?: InsertStoryDto) => {
  return axiosPostRequest<InsertStoryDto, Story>(getStandardStoryRoute(), data);
};

// Private story content item that will show in the room carousel
export const insertFeaturedStoryCall = (data?: InsertStoryDto) => {
  return axiosPostRequest<InsertStoryDto, Story>(getFeaturedStoryRoute(), data);
};

export const insertFeaturedContentCall = (
  data?: FeaturedContentPOSTRequestDTO
) => {
  return axiosPostRequest<InsertStoryDto, Story>(
    getFeaturedContentRoute(),
    data
  );
};

export const updateFeaturedContentCall = (
  data?: FeaturedContentPUTRequestDTO
) => {
  return axiosPutRequest<InsertStoryDto, Story>(
    getFeaturedContentRoute(),
    data
  );
};

export const updateFeaturedStoryCall = (
  storyId: string,
  updateStoryDto: UpdateFeaturedStoryDto
) => {
  return axiosPutRequest<UpdateFeaturedStoryDto, Story>(
    `${getFeaturedStoryRoute()}/${storyId}`,
    updateStoryDto
  );
};

export const deleteFeaturedStoryCall = (storyId: string) => {
  return axiosDeleteRequest<unknown, unknown>(
    `${getFeaturedStoryRoute()}/${storyId}`
  );
};

export const deleteFeaturedContentCall = (storyId: string) => {
  return axiosDeleteRequest<unknown, unknown>(
    `${getFeaturedContentRoute()}/${storyId}`
  );
};

export const updateStoryCall = (data?: InsertStoryDto, storyId?: string) => {
  return axiosPutRequest<InsertStoryDto, Story>(
    `${getStandardStoryRoute()}/${storyId}`,
    data
  );
};

export const deleteStoryCall = (storyId?: string) => {
  return axiosDeleteRequest(`${getStandardStoryRoute()}/${storyId}`);
};

export const reportStoryCall = (storyId: string) => {
  return axiosPatchRequest(`${getStandardStoryRoute()}/${storyId}/report`);
};

export const likeStoryCall = (storyId: string, likeDto: CreateLikeDto) => {
  return axiosPostRequest<CreateLikeDto, Story>(
    `${getStandardStoryRoute()}/${storyId}/like`,
    likeDto
  );
};

export const fetchStoriesByAuthorCall = (
  authorId: string,
  xMsContinuationToken?: string,
  numrecs?: number,
  sortBy?: StorySortBy
) => {
  return axiosPostPaginatedRequest<GetStoriesDto, Story[]>(
    `${getAllStoriesRoute()}/${numrecs ?? PAGE_SIZE}?state=published&sortby=${
      sortBy ?? StorySortBy.byDateCreated
    }&memberid=${authorId}`,
    {},
    xMsContinuationToken
  );
};

export const fetchStoriesByTopicCall = (
  topicName: string,
  xMsContinuationToken?: string,
  numrecs?: number,
  sortBy?: StorySortBy
) => {
  return axiosPostPaginatedRequest<GetStoriesDto, Story[]>(
    `${getAllStoriesRoute()}/${numrecs ?? PAGE_SIZE}?state=published&sortby=${
      sortBy ?? StorySortBy.byDateCreated
    }`,
    {
      topics: [
        {
          topic: topicName,
        } as any,
      ],
    },
    xMsContinuationToken
  );
};

export const moderatorDeleteStoryCall = (storyId: string) => {
  return axiosDeleteRequest(`${getStoriesRoute()}/moderator/story/${storyId}`);
};

export const moderatorUpdateStoryCall = (
  data: InsertStoryDto | undefined,
  storyId: string | undefined,
  moderationReason: string | undefined
) => {
  return axiosPutRequest<ModeratorEditStoryDto, Story>(
    `${getStoriesRoute()}/moderator/story/${storyId}`,
    { ...data, moderationReason: moderationReason }
  );
};

export const shareStoryToRoomsCall = (
  storyId: string,
  shareStoryDto: ShareStoryDto
) => {
  return axiosPostPaginatedRequest<ShareStoryDto, Post>(
    `${getStandardStoryRoute()}/${storyId}/share`,
    shareStoryDto
  );
};
